/**
 * @fileoverview This script replaces standard <select> dropdowns for SPH and CYL
 * with a custom, user-friendly table-based selector. It is designed to be
 * injected into a page without modifying the original HTML source, and it
 * preserves the functionality of the underlying form by synchronizing the
 * user's selection back to the hidden original dropdown.
 */

// A self-invoking function to create a private scope and avoid polluting the global namespace.
(() => {
  "use strict";

  // --- 1. Constants and Configuration ---
  // Using constants makes the code easier to read and manage.
  const CONFIG = {
    TARGET_SELECTORS: ".p-sph, .p-cyl",
    CUSTOM_CONTAINER_CLASS: "custom-select-container",
    STYLESHEET_ID: "custom-select-styles",
    MAIN_OPTIONS_CONTAINER_ID: "avpoptions-container__v2",
  };

  const COLORS = {
    TRIGGER_BG: "#323232",
    TRIGGER_TEXT: "rgb(200, 200, 200)",
    TRIGGER_BORDER: "#ddd",
    PANEL_BG: "#323232",
    PANEL_BORDER: "#ccc",
    HEADER_BG: "#0d6efd",
    HEADER_TEXT: "white",
    OPTION_HOVER_BG: "#0d6efd",
    EMPTY_CELL_TEXT: "#aaa",
  };

  // --- 2. Style Injection Module ---
  // A dedicated function for handling CSS keeps the setup clean.
  const StyleManager = {
    injectStyles: () => {
      if (document.getElementById(CONFIG.STYLESHEET_ID)) return;

      const style = document.createElement("style");
      style.id = CONFIG.STYLESHEET_ID;
      style.textContent = `
                .${CONFIG.CUSTOM_CONTAINER_CLASS} { position: relative; width: 100%; user-select: none; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; margin-top: 5px; }
                
                .custom-select-trigger { 
                    position: relative; 
                    display: flex; 
                    align-items: center; 
                    padding: 0 12px; 
                    padding-left: 16px !important; 
                    cursor: pointer; 
                    text-align: left; 
                    height: 43.7px !important; 
                    font-size: 14px; 
                    border-radius: 20px; 
                    background: ${COLORS.TRIGGER_BG} !important;
                    color: ${COLORS.TRIGGER_TEXT}; 
                    box-shadow: 0 0 6px -3px ${COLORS.PANEL_BORDER};
                }
                
                .custom-select-trigger::after {
                    content: ''; 
                    position: absolute; 
                    right: 0.8em; 
                    width: 7px; height: 7px;
                    border-left: 1.5px solid ${COLORS.EMPTY_CELL_TEXT}; 
                    border-bottom: 1.5px solid ${COLORS.EMPTY_CELL_TEXT}; 
                    transform: translateY(-50%) rotate(315deg); 
                    transition: transform 0.2s ease;
                }

                .${CONFIG.CUSTOM_CONTAINER_CLASS}.open .custom-select-trigger::after {
                    transform: translateY(-25%) rotate(135deg);
                }

                .custom-options-panel { 
                    display: none; 
                    position: absolute; 
                    top: 105%; 
                    left: 0; right: 0; 
                    background-color: ${COLORS.PANEL_BG};
                    border: 1px solid ${COLORS.PANEL_BORDER};
                    border-radius: 4px; 
                    box-shadow: 0 4px 10px rgba(0,0,0,0.1); 
                    z-index: 1000; 
                    max-height: 250px; 
                    overflow-y: auto;
                    scrollbar-width: thin;
                }

                .${CONFIG.CUSTOM_CONTAINER_CLASS}.open .custom-options-panel { display: block; }
                
                .custom-options-table { width: 100%; border-collapse: collapse; }
                .custom-options-table th, .custom-options-table td { padding: 10px; text-align: center; }
                .custom-options-table th { background-color: ${COLORS.HEADER_BG}; color: ${COLORS.HEADER_TEXT}; font-weight: bold; font-size: 1.4em; position: sticky; top: 0; z-index: 1; }
                .custom-options-table td[data-value] { cursor: pointer; font-size: 14px; font-weight: 500; }
                .custom-options-table td[data-value]:hover { background-color: ${COLORS.OPTION_HOVER_BG}; color: ${COLORS.HEADER_TEXT}; }
                .custom-options-table .empty-cell { color: ${COLORS.EMPTY_CELL_TEXT}; cursor: default; }
                .custom-options-table .special-option-cell { cursor: pointer; font-weight: 500; }
                .custom-options-table .special-option-cell:hover { background-color: ${COLORS.OPTION_HOVER_BG}; color: ${COLORS.HEADER_TEXT}; }
            `;
      document.head.appendChild(style);
    },
  };

  // --- 3. Data Processing Module ---
  // Encapsulates the logic for parsing select options.
  const OptionProcessor = {
    pairOptions: (originalOptions) => {
      const valueMap = new Map();
      const specialOptions = new Map();

      originalOptions.forEach((option) => {
        const { value: originalValue, text } = option;
        const num = parseFloat(originalValue);

        if (
          isNaN(num) ||
          originalValue.includes("--") ||
          originalValue.includes("PL") ||
          num === 0
        ) {
          if (!specialOptions.has(originalValue))
            specialOptions.set(originalValue, text.trim());
        } else {
          const absValue = Math.abs(num);
          if (!valueMap.has(absValue))
            valueMap.set(absValue, { pos: null, neg: null });
          const pair = valueMap.get(absValue);
          const data = { text: text.trim(), value: originalValue };
          num > 0 ? (pair.pos = data) : (pair.neg = data);
        }
      });

      const sortedPairs = Array.from(valueMap.entries()).sort(
        (a, b) => a[0] - b[0]
      );
      const specialList = Array.from(specialOptions.entries()).map(
        ([value, text]) => ({ value, text })
      );

      return { sortedPairs, specialOptions: specialList };
    },
  };

  // --- 4. UI Component Creation Module ---
  // This "factory" creates all parts of the custom select component.
  const CustomSelectFactory = {
    create: (container) => {
      const originalSelect = container.querySelector("select");
      const originalSelectWrapper = container.querySelector(
        ".ap-options__select"
      );
      if (!originalSelect || !originalSelectWrapper) return;
      if (container.querySelector(`.${CONFIG.CUSTOM_CONTAINER_CLASS}`)) return;

      originalSelectWrapper.style.display = "none";
      const optionsData = OptionProcessor.pairOptions(
        Array.from(originalSelect.options)
      );

      // Build component parts
      const customContainer = CustomSelectFactory.buildContainer();
      const trigger = CustomSelectFactory.buildTrigger(originalSelect);
      const panel = CustomSelectFactory.buildPanel(optionsData);

      // Assemble and append
      customContainer.append(trigger, panel);
      container.appendChild(customContainer);

      // Attach behaviors
      CustomSelectFactory.addEventListeners(
        customContainer,
        trigger,
        panel,
        originalSelect
      );
    },

    buildContainer: () => {
      const div = document.createElement("div");
      div.className = CONFIG.CUSTOM_CONTAINER_CLASS;
      return div;
    },

    buildTrigger: (originalSelect) => {
      const div = document.createElement("div");
      div.className = "custom-select-trigger";
      div.textContent =
        originalSelect.options[originalSelect.selectedIndex].text;
      return div;
    },

    buildPanel: ({ sortedPairs, specialOptions }) => {
      const panel = document.createElement("div");
      panel.className = "custom-options-panel";

      const table = document.createElement("table");
      table.className = "custom-options-table";
      table.createTHead().innerHTML = "<tr><th>-</th><th>+</th></tr>";

      const tbody = table.createTBody();

      // Add special options first
      specialOptions
        .sort((a, b) =>
          a.text.localeCompare(b.text, undefined, { numeric: true })
        )
        .reverse()
        .forEach((opt) => {
          const row = tbody.insertRow();
          const cell = row.insertCell();
          cell.colSpan = 2;
          cell.className = "special-option-cell";
          cell.dataset.value = opt.value;
          cell.textContent = opt.text;
        });

      // Add paired values
      sortedPairs.forEach(([_, pair]) => {
        const row = tbody.insertRow();
        const negCell = row.insertCell();
        const posCell = row.insertCell();

        if (pair.neg) {
          negCell.textContent = pair.neg.text;
          negCell.dataset.value = pair.neg.value;
        } else {
          negCell.textContent = "-";
          negCell.className = "empty-cell";
        }

        if (pair.pos) {
          posCell.textContent = pair.pos.text;
          posCell.dataset.value = pair.pos.value;
        } else {
          posCell.textContent = "-";
          posCell.className = "empty-cell";
        }
      });

      panel.appendChild(table);
      return panel;
    },

    addEventListeners: (container, trigger, panel, select) => {
      trigger.addEventListener("click", (e) => {
        e.stopPropagation();
        document
          .querySelectorAll(`.${CONFIG.CUSTOM_CONTAINER_CLASS}.open`)
          .forEach((openContainer) => {
            if (openContainer !== container)
              openContainer.classList.remove("open");
          });
        container.classList.toggle("open");
      });

      panel.addEventListener("click", (e) => {
        const targetCell = e.target.closest("[data-value]");
        if (!targetCell) return;

        const newValue = targetCell.dataset.value;
        const newText = targetCell.textContent;

        // Sync with original select element
        select.value = newValue;
        for (const option of select.options) {
          option.selected = option.value === newValue;
        }
        select.dispatchEvent(new Event("change", { bubbles: true }));

        // Update UI and close
        trigger.textContent = newText;
        container.classList.remove("open");
      });
    },
  };

  // --- 5. Main Application Logic ---
  const App = {
    init: () => {
      StyleManager.injectStyles();
      document
        .querySelectorAll(CONFIG.TARGET_SELECTORS)
        .forEach(CustomSelectFactory.create);
    },

    observeDOMChanges: () => {
      const mainContainer = document.getElementById(
        CONFIG.MAIN_OPTIONS_CONTAINER_ID
      );
      if (!mainContainer) {
        console.warn(
          `Observer target #${CONFIG.MAIN_OPTIONS_CONTAINER_ID} not found.`
        );
        return;
      }

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.addedNodes.length) {
            // Efficiently check if relevant nodes were added before re-running
            const hasRelevantNodes = Array.from(mutation.addedNodes).some(
              (node) =>
                node.nodeType === 1 &&
                (node.matches(CONFIG.TARGET_SELECTORS) ||
                  node.querySelector(CONFIG.TARGET_SELECTORS))
            );
            if (hasRelevantNodes) {
              App.init();
              break; // No need to check other mutations if we already re-initialized
            }
          }
        }
      });

      observer.observe(mainContainer, { childList: true, subtree: true });
    },

    addGlobalListeners: () => {
      document.addEventListener("click", () => {
        document
          .querySelectorAll(`.${CONFIG.CUSTOM_CONTAINER_CLASS}.open`)
          .forEach((c) => c.classList.remove("open"));
      });
    },
  };

  // --- 6. Execution ---
  // Run the app when the DOM is ready.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      App.init();
      App.observeDOMChanges();
      App.addGlobalListeners();
    });
  } else {
    // DOM is already loaded
    App.init();
    App.observeDOMChanges();
    App.addGlobalListeners();
  }
})();
